import type {
  CandidatesArtifact,
  RundownArtifact,
  ScriptArtifact,
  VerdictArtifact,
  SynthesisManifest,
  PublishManifest,
  RunState,
} from "../types/contracts";

/**
 * Derived view model for the public proof page.
 *
 * The UI never consumes raw contract artifacts directly — this adapter
 * assembles them into a single EditionViewModel with only the fields the
 * UI needs. All source fields come from frozen contract artifacts; no
 * invented backend fields exist here.
 */

export type PageState =
  | "loading"
  | "no_edition"
  | "blocked"
  | "partial_publish"
  | "published"
  | "corrected";

export interface AgentStage {
  label: string;
  name: string;
  timestamp: string; // ISO 8601
  duration_seconds: number;
  status: "done" | "blocked" | "skipped";
  detail: string;
}

export interface RundownEntry {
  rank: number;
  story_title: string;
  estimated_seconds: number;
  angle: string;
  selection_reason: string;
}

export interface LedgerEntry {
  line_id: string;
  speaker: string;
  text: string;
  claim_id: string;
  verdict: "PASS" | "REWRITE" | "BLOCK";
  source_id: string;
  source_url: string;
  excerpt: string;
  snapshot_path: string;
  retrieved_at: string;
}

export interface BlockedClaimExample {
  line_id: string;
  reason: string;
  action: string;
}

export interface TranscriptLine {
  line_id: string;
  speaker: string;
  line_type: "fact" | "transition";
  text: string;
  claim_ids: string[];
}

export interface PreviousEdition {
  episode_slug: string;
  title: string;
  published_at: string;
  duration_seconds: number;
  story_count: number;
  source_count: number;
  claim_counts: { pass: number; rewrite: number; block: number };
  audio_url: string | null;
}

export interface EditionViewModel {
  page_state: PageState;
  run_id: string;
  edition_key: string;
  run_state: RunState;
  title: string;
  caption: string;
  episode_slug: string;
  audio_url: string | null;
  published_at: string; // ISO 8601 — display as IST
  duration_seconds: number;
  story_count: number;
  source_count: number;
  claim_counts: { pass: number; rewrite: number; block: number };
  correction_note: string | null;
  corrected_at: string | null;
  next_scheduled: string | null; // ISO 8601
  blocked_reason: string | null;
  telegram_target: string | null;
  telegram_url: string | null;
  transcript: TranscriptLine[];
  rundown: RundownEntry[];
  timeline: AgentStage[];
  ledger: LedgerEntry[];
  blocked_examples: BlockedClaimExample[];
  previous_editions: PreviousEdition[];
  feed_health: { feed_id: string; status: string; item_count: number }[];
  candidate_count: number;
  selected_count: number;
  excluded_count: number;
}

/**
 * Raw fixture input — all fields are from frozen contract artifacts.
 * The only non-contract fields are UI-only metadata (audio_url, timeline
 * durations, blocked examples, previous editions, correction info) which
 * are display-layer concerns, not invented backend fields.
 */
export interface EditionFixture {
  candidates: CandidatesArtifact;
  rundown: RundownArtifact;
  script: ScriptArtifact;
  verdict: VerdictArtifact;
  synthesis: SynthesisManifest;
  publish: PublishManifest;
  // UI-only metadata (not contract fields):
  audio_url: string | null;
  timeline: AgentStage[];
  blocked_examples: BlockedClaimExample[];
  previous_editions: PreviousEdition[];
  correction_note: string | null;
  corrected_at: string | null;
  next_scheduled: string | null;
  blocked_reason: string | null;
  run_state_override: RunState | null;
}

export function buildEditionViewModel(fixture: EditionFixture): EditionViewModel {
  const { candidates, rundown, script, verdict, publish } = fixture;

  // Determine page state
  let page_state: PageState = "published";

  if (fixture.run_state_override) {
    const rs = fixture.run_state_override;
    if (rs === "NO_EDITION") page_state = "no_edition";
    else if (rs === "BLOCKED") page_state = "blocked";
    else if (rs === "PUBLISH_PARTIAL") page_state = "partial_publish";
    else if (rs === "FAILED") page_state = "blocked";
  }

  if (fixture.correction_note) {
    page_state = "corrected";
  }

  // Check destinations for partial publish
  const destinations = publish.destinations;
  const hasFailed = destinations.some((d) => d.state === "FAILED");
  const hasSucceeded = destinations.some((d) => d.state === "SUCCEEDED");
  if (hasFailed && hasSucceeded && page_state === "published") {
    page_state = "partial_publish";
  }

  // Build rundown entries from script lines + candidates
  const candidateMap = new Map(candidates.candidates.map((c) => [c.story_id, c]));
  const rundownEntries: RundownEntry[] = rundown.selected.map((item) => {
    const story = candidateMap.get(item.story_id);
    return {
      rank: item.rank,
      story_title: story?.title ?? item.story_id,
      estimated_seconds: item.estimated_seconds,
      angle: item.angle,
      selection_reason: item.selection_reason,
    };
  });

  // Build ledger from script lines + verdict claims
  const claimVerdictMap = new Map(verdict.claims.map((v) => [v.claim_id, v]));
  const ledger: LedgerEntry[] = [];

  for (const line of script.lines) {
    if (line.line_type !== "fact") continue;
    for (const claimId of line.claim_ids) {
      const cv = claimVerdictMap.get(claimId);
      if (!cv) continue;
      const ev = cv.evidence[0];
      if (!ev) continue;
      // Find retrieved_at from candidates
      const claim = script.claims.find((c) => c.claim_id === claimId);
      const candidate = claim
        ? candidates.candidates.find((c) => c.story_id === claim.story_id)
        : undefined;
      ledger.push({
        line_id: line.line_id,
        speaker: line.speaker,
        text: line.text,
        claim_id: cv.claim_id,
        verdict: cv.verdict,
        source_id: ev.source_id,
        source_url: cv.source_urls[0] ?? "",
        excerpt: ev.excerpt,
        snapshot_path: ev.snapshot_path,
        retrieved_at: candidate?.retrieved_at ?? candidates.created_at,
      });
    }
  }

  // Build telegram URL from target
  const telegramUrl = publish.telegram_target
    ? `https://t.me/c/${publish.telegram_target.replace("telegram:-", "").replace("100", "")}`
    : null;

  return {
    page_state,
    run_id: publish.run_id,
    edition_key: publish.edition_key,
    run_state: fixture.run_state_override ?? "PUBLISHED",
    title: publish.title,
    caption: publish.caption,
    episode_slug: publish.episode_slug,
    audio_url: fixture.audio_url,
    published_at: publish.created_at,
    duration_seconds: script.estimated_seconds,
    story_count: rundown.selected.length,
    source_count: publish.source_count,
    claim_counts: publish.claim_counts,
    correction_note: fixture.correction_note,
    corrected_at: fixture.corrected_at,
    next_scheduled: fixture.next_scheduled,
    blocked_reason: fixture.blocked_reason,
    telegram_target: publish.telegram_target,
    telegram_url: telegramUrl,
    transcript: script.lines.map((line) => ({
      line_id: line.line_id,
      speaker: line.speaker,
      line_type: line.line_type,
      text: line.text,
      claim_ids: line.claim_ids,
    })),
    rundown: rundownEntries,
    timeline: fixture.timeline,
    ledger,
    blocked_examples: fixture.blocked_examples,
    previous_editions: fixture.previous_editions,
    feed_health: candidates.feed_health.map((f) => ({
      feed_id: f.feed_id,
      status: f.status,
      item_count: f.item_count,
    })),
    candidate_count: candidates.candidates.length,
    selected_count: rundown.selected.length,
    excluded_count: rundown.excluded.length,
  };
}
